import React, { useState, useEffect, useCallback } from "react";
import { Card, Col, Row, Input, Pagination } from "antd";
import {
  ActiveUsers,
  TotalTimeSpent,
  GlobalAnalytics,
  UserAnalytics,
  ReturnUsers,
  RecentUsers,
} from "./components";
import http from "../../../../services/http";
import { UserListSkeleton } from "../../../../components/page-skeleton";
import { useSelector } from "react-redux";
import "./styles.scss";

const USERS_PAGE_SIZE = 20;

function Analytics() {
  // STATES
  const [preview, setPreview] = useState(false);
  const [usersLoader, setUsersLoader] = useState(true);
  const [users, setusers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedUser, setselectedUser] = useState(null);

  // CONST VALS
  const { profile } = useSelector((state) => state.UserReducer);
  const isAdmin = profile?.is_superuser;

  const getUsersHelper = useCallback(
    async (pageNum = 1) => {
      setUsersLoader(true);
      try {
        const offset = (pageNum - 1) * USERS_PAGE_SIZE;
        const params = { limit: USERS_PAGE_SIZE, offset };
        if (search.trim()) params.search = search.trim();
        const { data } = await http.get("users", params);
        const list = data?.data ?? (Array.isArray(data) ? data : []);
        const totalCount = data?.total ?? list.length;
        setusers(list);
        setTotal(totalCount);
        setselectedUser((prev) =>
          list.length > 0 && !list.some((u) => u.id === prev) ? list[0].id : prev,
        );
      } catch {
        setusers([]);
        setTotal(0);
      } finally {
        setUsersLoader(false);
      }
    },
    [search],
  );

  useEffect(() => {
    if (!isAdmin) return;
    setPreview(true);
    setusers([]);
    setTotal(0);
    setPage(1);
  }, [isAdmin]);

  useEffect(() => {
    if (!preview || !isAdmin) return;
    getUsersHelper(page);
  }, [preview, isAdmin, page, search]);

  const onSearch = (value) => {
    setSearch(value?.trim?.() ?? searchInput.trim());
    setPage(1);
  };

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  if (!preview) return "";

  return (
    <div className="analytics-wrapper">
      <h2 className="page-title">Admin Analytics</h2>
      <GlobalAnalytics />
      <Row gutter={[16, 0]}>
        <Col xs={24} lg={14}>
          <ActiveUsers />
        </Col>
        <Col xs={24} lg={10}>
          <RecentUsers />
        </Col>
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} lg={12}>
          <TotalTimeSpent />
        </Col>
        <Col xs={24} lg={12}>
          <ReturnUsers />
        </Col>
      </Row>
      <Card className="users-detail">
        <Row>
          <Col xs={24} lg={6}>
            <div className="users-list">
              <h3 className="title">Select User</h3>
              <Input.Search
                placeholder="Name or email"
                allowClear
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onSearch={onSearch}
                onPressEnter={() => onSearch(searchInput)}
                className="users-list-search"
                loading={usersLoader}
              />
              <ul className="users-list-items">
                {usersLoader ? (
                  <UserListSkeleton rows={8} />
                ) : users.length === 0 ? (
                  <li className="users-list-empty">
                    {search ? "No users match your search." : "No users yet."}
                  </li>
                ) : (
                  users.map((user) => (
                    <li
                      key={user.id}
                      onClick={() => setselectedUser(user.id)}
                      className={selectedUser === user.id ? "selected" : ""}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setselectedUser(user.id);
                        }
                      }}
                    >
                      <span className="users-list-name">
                        {user.username?.trim() || user.email?.trim() || "—"}
                      </span>
                      <span className="users-list-email">
                        {user.username?.trim() ? user.email?.trim() || "—" : "—"}
                      </span>
                    </li>
                  ))
                )}
              </ul>
              {!usersLoader && total > 0 && (
                <div className="users-list-pagination">
                  <Pagination
                    size="small"
                    current={page}
                    total={total}
                    pageSize={USERS_PAGE_SIZE}
                    onChange={onPageChange}
                    showSizeChanger={false}
                    showLessItems
                    showTotal={(t) => `${t} users`}
                  />
                </div>
              )}
            </div>
          </Col>
          <Col xs={24} lg={18}>
            <h3 className="title">Analytics</h3>
            <UserAnalytics selectedUser={selectedUser} />
          </Col>
        </Row>
      </Card>
    </div>
  );
}

export default Analytics;
